import React, { useContext, useMemo } from 'react';

import { getChartConfigByKey } from './helpers/chartOption.helpers';

import ChartConfiguration from './ChartConfiguration';
import { datasetsHaveNoData } from '../../utils/chart_utils/chart_utils';
import getMaxValueAcrossDatasets from './helpers/useMaxValueInDataset';

// Chart Context is used to distribute important chart data throughout various parts of a single
// chart component without forcing a prop spreading pattern at the top level.
// This also makes re renders more efficient, and encapsulates configuration logic
const ChartContext = React.createContext({
  chartID: null,
  chartType: 'line',
  chartConfig: ChartConfiguration.LINE,
  datasets: [],
  labels: [],
  dropdownOptions: [],
  noDataAvailable: true,
  units: null,
  maxY: 0,
})

export const ChartProvider = ({
  children,
  options = {},
  chartID = null,
  chartType = 'line',
  chartConfiguration = 'line',
  datasets: rawDatasets = [],
  labels = [],
  dropdownOptions = [],
  units = null,
  prepareDataset = dataset => dataset,
  usePaddedMax = false,
}) => {
  const chartConfig = useMemo(() => getChartConfigByKey(chartConfiguration), [chartConfiguration]);
  const datasets = useMemo(() => rawDatasets.map(prepareDataset), [prepareDataset, rawDatasets]);
  const noDataAvailable = useMemo(() => datasetsHaveNoData(datasets), [datasets]);

  const maxY = useMemo(() => getMaxValueAcrossDatasets(datasets, usePaddedMax), [datasets, usePaddedMax]);

  const value = useMemo(() => ({
    chartID,
    chartType,
    chartConfig,
    datasets,
    labels,
    dropdownOptions,
    noDataAvailable,
    units,
    maxY,
    options,
  }), [
    chartID,
    chartType,
    chartConfig,
    datasets,
    labels,
    dropdownOptions,
    noDataAvailable,
    units,
    maxY,
    options
  ]);

  return (
    <ChartContext.Provider
      value={value}
    >
      { children }
    </ChartContext.Provider>
  )
}

export const useChartData = () => useContext(ChartContext);

export default useChartData;
